import React, { useCallback, useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { adBreakColumns, getAdBreakHref } from "./tableParams/adBreakColumns";
import FastAPI from "../../../api/FastAPI";
import useFASTLayout from "../../../hooks/useFASTLayout";
import CreateButton from "../../common/CreateButton";
import DataTable from "../../dataTable/DataTable";
import { adBreakNewPath, adBreakPath } from "../../routes/routes";
import useFastRowActionList from "../useFastRowActionList";

import type { JSX } from "react";

import type {
  AdBreakItem,
  GetAdBreakResponse,
} from "../../../api/FastAPI.types";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";
import type { FastRowRights } from "../useFastRowActionList.types";

function selectAdBreakRights(details: GetAdBreakResponse): FastRowRights {
  return details.data;
}

function AdBreaks(): JSX.Element {
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const { t } = useTranslation("editor-fast-channel");

  useFASTLayout({
    title: t("ad-breaks"),
  });

  const queryClient = useQueryClient();

  const getAdBreaksListQuery = FastAPI.generateQuery({
    fn: FastAPI.getAdBreaksList,
    params: [channelId],
  });

  const { isLoading, isError, refetch, data } = useQuery(getAdBreaksListQuery);

  const { mutate: deleteAdbreak } = useMutation({
    mutationFn: async (adBreakId: string) =>
      FastAPI.deleteAdBreak(channelId, adBreakId),
    onSuccess: () => {
      toast.success(t("adBreakDeleteSuccess"));
      queryClient.invalidateQueries(getAdBreaksListQuery.queryKey);
    },
    onError: (e) => {
      toast.error(t("adBreakDeleteError"));
      console.error(e);
    },
  });

  const { mutate: archiveAdbreak } = useMutation({
    mutationFn: async (adBreakId: string) =>
      FastAPI.archiveAdbreak(channelId, adBreakId),
    onSuccess: () => {
      toast.success(t("adBreakArchiveSuccess"));
      queryClient.invalidateQueries(getAdBreaksListQuery.queryKey);
    },
    onError: (e) => {
      toast.error(t("adBreakArchiveError"));
      console.error(e);
    },
  });

  const { mutate: duplicateAdbreak } = useMutation({
    mutationFn: async (adbreakId: string) =>
      FastAPI.duplicateAdbreak(channelId, adbreakId),
    onSuccess: () => {
      toast.success(t("adbreakDuplicateSuccess"));
      queryClient.invalidateQueries(getAdBreaksListQuery.queryKey);
    },
    onError: (e) => {
      toast.error(t("adbreakDuplicateError"));
      console.error(e);
    },
  });

  const getDetailsQuery = useCallback(
    (adBreak: AdBreakItem) =>
      FastAPI.generateQuery({
        fn: FastAPI.getAdBreak,
        params: [channelId, adBreak.uuid],
      }),
    [channelId],
  );

  const getEditPath = useCallback(
    (adBreak: AdBreakItem): string => adBreakPath(channelId, adBreak.uuid),
    [channelId],
  );

  const loadActions = useFastRowActionList({
    labelPrefix: "adbreak",
    getDetailsQuery,
    selectRights: selectAdBreakRights,
    getEditPath,
    onDuplicate: duplicateAdbreak,
    onArchive: archiveAdbreak,
    onDelete: deleteAdbreak,
  });

  const getRowHref = useCallback(
    (adBreak: AdBreakItem): string => getAdBreakHref(channelId, adBreak),
    [channelId],
  );

  const columnList = useMemo(
    () => adBreakColumns({ t, channelId, loadActions }),
    [t, channelId, loadActions],
  );

  const createAdBreakButton = (
    <CreateButton href={adBreakNewPath(channelId)} label={t("createAdBreak")} />
  );

  return (
    <div className="w-full min-w-0">
      <DataTable
        columns={columnList}
        data={data?.data ?? []}
        defaultSorting={{ id: "updated_at", desc: true }}
        getRowHref={getRowHref}
        headerActions={createAdBreakButton}
        isError={isError}
        isLoading={isLoading}
        onRetry={refetch}
        title={t("adBreakList")}
      />
    </div>
  );
}

export default AdBreaks;
